import React, {useEffect, useRef, useState} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ImageDragAndDrop from '../components/ImageDnD';
import {apiFetch} from '../utils/api';

const steps = [
  'Información básica',
  'Imagen',
  'Ingredientes',
  'Requisitos previos (Opcional)',
  'Pasos de la preparación',
  'Etiquetas',
];

const NewRecipePage = () => {
  // Estados para el stepper y campos básicos
  const [activeStep, setActiveStep] = useState(0);
  const [recipeName, setRecipeName] = useState('');
  const [description, setDescription] = useState('');
  const [peopleNumber, setPeopleNumber] = useState(1);
  const [isPrivate, setIsPrivate] = useState(false);
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');

  // Ingredientes con búsqueda
  const [ingredients, setIngredients] = useState([]);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIngredient, setSelectedIngredient] = useState(null);

  // Estados para entrada de cantidad y unidad
  const [temporaryQuantity, setTemporaryQuantity] = useState('');
  const [temporaryUnit, setTemporaryUnit] = useState('');
  const quantityInputRef = useRef(null);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [conversionAmount, setConversionAmount] = useState('');
  const [customUnit, setCustomUnit] = useState('');

  const [prerequisites, setPrerequisites] = useState([]);
  const [preparationSteps, setPreparationSteps] = useState([]);
  const [tags, setTags] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  // Opciones de unidad según medida base
  const unitMeasureOptions = {
    kg: ['kg', 'gr', 'Cucharadas', 'Pizca'],
    L: ['L', 'ml', 'Cucharadas', 'Chorro'],
    dc: ['Unidades'],
    "100 g": ['gr', 'Cucharadas', 'Pizca'],
    "100 ml": ['ml', 'Cucharadas', 'Chorro'],
  };

  // Detectar si la unidad elegida requiere conversión
  const requiresConversion = (unit, base) => {
    const normalized = unit.toLowerCase();
    return base === 'kg' || base === '100 g'
        ? !['kg', 'gr'].includes(normalized)
        : base === 'l' || base === '100 ml'
            ? !['l', 'ml'].includes(normalized)
            : false;
  };

  const baseUnit = selectedIngredient?.unit_measure.includes('ml') ? 'L'
      : selectedIngredient?.unit_measure.includes('g') ? 'kg'
          : selectedIngredient?.unit_measure.includes('L') ? 'L'
              : selectedIngredient?.unit_measure.includes('g') ? 'kg'
                  : null;

  const showConversionField = temporaryUnit && baseUnit && requiresConversion(temporaryUnit, baseUnit);
  const isCustomUnit = temporaryUnit === 'Otras';

  useEffect(() => {
    // Validar cada paso
    switch (activeStep) {
      case 0:
        setCanProceed(recipeName.trim() !== '' && description.trim() !== '' && peopleNumber > 0);
        break;
      case 1:
        setCanProceed(image !== null || imageUrl.trim() !== '');
        break;
      case 2:
        setCanProceed(ingredients.length > 0);
        break;
      case 3:
        setCanProceed(true); // Paso opcional
        break;
      case 4:
        setCanProceed(preparationSteps.length > 0);
        break;
      case 5:
        setCanProceed(true); // Paso opcional
        break;
      default:
        setCanProceed(false);
    }
  }, [activeStep, recipeName, description, peopleNumber, image, imageUrl, ingredients, preparationSteps]);

  // Llamada a la API de productos (endpoint /product/) y mapear resultados como opciones de ingredientes, límite a 10 y limpiar anteriores
  useEffect(() => {
    // Si el término está vacío, reseteamos opciones
    if (!searchTerm) {
      setIngredientOptions([]);
      return;
    }

    // Limpiamos opciones anteriores antes de lanzar nueva búsqueda
    setIngredientOptions([]);

    const controller = new AbortController();

    const backendUrl = process.env.REACT_APP_INGREDIENTS_API_URL || "http://localhost:8000";

    fetch(
        `${backendUrl}/product/?name=${encodeURIComponent(searchTerm)}&per_page=25`,
        { signal: controller.signal }
    )
        .then(res => res.json())
        .then(data => {
          const list = Array.isArray(data)
              ? data
              : Array.isArray(data.results)
                  ? data.results
                  : [];
          setIngredientOptions(list);
        })
        .catch(err => {
          if (err.name !== 'AbortError') console.error(err);
        });

    return () => controller.abort();
  }, [searchTerm]);

  // Handler al seleccionar un producto
  const handleSelectIngredient = (e, value) => {
    if (value && typeof value === 'object') {
      setSelectedIngredient(value);
      setQuantityDialogOpen(true);
      setTemporaryQuantity('');
      setTemporaryUnit('');
    }
  };

  // Confirmar cantidad y agregar a ingredientes
  const handleQuantitySubmit = () => {
    const qty = parseFloat(temporaryQuantity);
    const conv = parseFloat(conversionAmount);
    const unitLabel = isCustomUnit ? customUnit : temporaryUnit;

    if (!isNaN(qty) && qty > 0 && unitLabel && selectedIngredient) {
      setIngredients(prev => [
        ...prev,
        {
          id: selectedIngredient.id,
          providerId: selectedIngredient.provider.id,
          name: selectedIngredient.name,
          provider: selectedIngredient.provider.name,
          unitMeasure: selectedIngredient.unit_measure,
          quantity: qty,
          selectedUnit: `${unitLabel}`,
          equivalentAmountInBaseUnit: (showConversionField || isCustomUnit) && !isNaN(conv) ? conv : undefined,
          customUnitName: isCustomUnit ? customUnit : undefined
        },
      ]);
      // Reset campos
      setSearchTerm('');
      setIngredientOptions([]);
      setSelectedIngredient(null);
      setTemporaryQuantity('');
      setTemporaryUnit('');
      setConversionAmount('');
      setCustomUnit('');
      setQuantityDialogOpen(false);
    }
  };

  // Eliminar ingrediente
  const handleDeleteIngredient = keyToDelete => {
    setIngredients(prev => prev.filter(item => item.uniqueKey !== keyToDelete));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return recipeName.trim() !== '' && description.trim() !== '' && peopleNumber > 0;
      case 1:
        return image !== null || imageUrl.trim() !== '';
      case 2:
        return ingredients.length > 0;
      case 3:
        return true;
      case 4:
        return preparationSteps.length > 0;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      let newCompletedSteps = [];
      for (let i = 0; i <= activeStep; i++) {
        newCompletedSteps.push(true);
      }
      setCompletedSteps(newCompletedSteps);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => {
    let canNavigate = true;
    for (let i = 0; i < step; i++) {
      if (!validateStep(i)) {
        canNavigate = false;
        break;
      }
    }

    if (canNavigate) {
      setActiveStep(step);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setRecipeName('');
    setDescription('');
    setPeopleNumber(1);
    setIsPrivate(false);
    setImage(null);
    setImageUrl('');
    setIngredients([]);
    setPrerequisites([]);
    setPreparationSteps([]);
    setTags([]);
  };

  const handleAddChip = (inputValue, setFunc) => {
    if (inputValue.trim() !== '') {
      setFunc((prevItems) => [...prevItems, inputValue.trim()]);
    }
  };

  const handleDeleteChip = (chipToDelete, setFunc) => {
    setFunc((prevItems) => prevItems.filter((chip) => chip !== chipToDelete));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const ingredientsForApi = ingredients.map(item => ({
      id: item.id,
      providerId: item.providerId,
      name: item.name,
      unitMeasure: item.unitMeasure,
      quantity: item.quantity,
      selectedUnit: item.selectedUnit,
      equivalentAmountInBaseUnit: item.equivalentAmountInBaseUnit,
      customUnitName: item.customUnitName
    }));

    const imageToSend = image ? image : imageUrl;

    const recipeData = {
      title: recipeName,
      description,
      peopleNumber,
      private: isPrivate,
      image: imageToSend,
      ingredients: ingredientsForApi,
      prerequisites,
      preparationSteps,
      tags,
    };

    try {
      const response = await apiFetch('/api/recipes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recipeData),
      });

      if (!response.ok) {
        throw new Error('Failed to create recipe');
      }

      const result = await response.json();
      console.log('Recipe created successfully:', result);

      handleReset(); // Resetear el formulario tras el éxito
      setOpenDialog(true); // Abrir el diálogo
    } catch (error) {
      console.error('Error creating recipe:', error);
      alert('Hubo un error al crear la receta.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
            <>
              <TextField
                  fullWidth
                  label="Nombre de la receta"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canProceed) handleNext();
                  }}
                  sx={{ mb: 2 }}
              />
              <TextField
                  fullWidth
                  label="Descripción"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={4}
                  sx={{ mb: 2 }}
              />
              <TextField
                  fullWidth
                  label="Número de personas"
                  value={peopleNumber}
                  onChange={(e) => setPeopleNumber(e.target.value)}
                  variant="outlined"
                  type="number"
                  InputProps={{ inputProps: { min: 1 }, type: 'number' }}
                  sx={{ mb: 2 }}
              />
              <FormControlLabel
                  control={
                    <Checkbox
                        checked={isPrivate}
                        onChange={(e) => setIsPrivate(e.target.checked)}
                        color="primary"
                    />
                  }
                  label="Hacer esta receta privada"
              />
            </>
        );
      case 1:
        return (
            <>
              {
                (!image && !imageUrl) || (image && !imageUrl) ?
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Sube/Arrastra una imagen
                      </Typography>
                      <ImageDragAndDrop image={image} setImage={setImage} />
                    </>
                    :
                    ""
              }
              {!image && !imageUrl ? <Divider sx={{ my: 2 }} > o </Divider> : ""}

              {
                (!image && !imageUrl) || (imageUrl && !image) ?
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Ingresa un enlace
                      </Typography>

                      <TextField
                          fullWidth
                          label="Enlace de la imagen"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                          variant="outlined"
                          sx={{ mt: 2 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && canProceed) handleNext();
                          }}
                      />
                    </>
                    :
                    ""
              }

            </>
        );
      case 2:
        // Opciones dinámicas de unidad según medida base
        const measurementOptions = selectedIngredient
            ? [...(unitMeasureOptions[selectedIngredient.unit_measure] || [selectedIngredient.unit_measure]), 'Otras']
            : ['Otras'];
        return (
            <>
              <Autocomplete
                  freeSolo
                  options={ingredientOptions}
                  inputValue={searchTerm}
                  getOptionLabel={opt => typeof opt === 'string' ? opt : `${opt.name} (${opt.unit_measure})`}
                  filterOptions={x => x}
                  onInputChange={(e, value) => setSearchTerm(value)}
                  onChange={handleSelectIngredient}
                  renderInput={params => (
                      <TextField
                          {...params}
                          label="Buscar ingrediente"
                          placeholder="Escribe para buscar"
                          variant="outlined"
                          sx={{ mb: 2 }}
                      />
                  )}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {ingredients.map(item => (
                    <Chip
                        key={item.uniqueKey}
                        label={`${item.name} – ${item.quantity} ${item.selectedUnit}`}
                        onDelete={() => handleDeleteIngredient(item.uniqueKey)}
                    />
                ))}
              </Box>

              <Dialog open={quantityDialogOpen} onClose={() => setQuantityDialogOpen(false)}>
                <DialogTitle>¿Qué cantidad?</DialogTitle>
                <DialogContent>
                  <TextField
                      inputRef={quantityInputRef}
                      margin="dense"
                      label="Cantidad"
                      type="number"
                      inputProps={{ type: 'number'}}
                      fullWidth
                      variant="outlined"
                      InputProps={{ inputProps: { min: 0, step: 'any' } }}
                      value={temporaryQuantity}
                      onChange={e => setTemporaryQuantity(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleQuantitySubmit();
                      }}
                      sx={{ mb: 2 }}
                  />
                  <Autocomplete
                      options={measurementOptions}
                      value={temporaryUnit}
                      onChange={(e, val) => setTemporaryUnit(val || '')}
                      inputValue={temporaryUnit}
                      onInputChange={(e, val) => setTemporaryUnit(val)}
                      freeSolo={false}
                      renderInput={params => (
                          <TextField {...params} label="Unidad" variant="outlined" />
                      )}
                  />
                  {
                      (showConversionField || isCustomUnit || temporaryUnit === 'Otras') && (
                          <>
                            {(isCustomUnit || temporaryUnit === 'Otras') && (
                                <TextField
                                    margin="dense"
                                    label="Nombre de la unidad personalizada"
                                    fullWidth
                                    variant="outlined"
                                    value={customUnit}
                                    onChange={e => setCustomUnit(e.target.value)}
                                    sx={{ mt: 2 }}
                                />
                            )}
                            <TextField
                                margin="dense"
                                label={`Equivalente estimado (${baseUnit})`}
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                value={conversionAmount}
                                onChange={e => setConversionAmount(e.target.value)}
                                helperText={`Introduce la cantidad aproximada en ${baseUnit} para poder calcular el precio.`}
                                sx={{ mt: 2 }}
                            />
                          </>
                      )
                  }
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setQuantityDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleQuantitySubmit} disabled={!(parseFloat(temporaryQuantity) > 0 && temporaryUnit)}>
                    Aceptar
                  </Button>
                </DialogActions>
              </Dialog>
            </>
        );
      case 3:
        return (
            <>
              <TextField
                  fullWidth
                  label="Requisitos previos"
                  placeholder="Añade un requisito previo y presiona Enter (Opcional)"
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChip(e.target.value, setPrerequisites);
                      e.target.value = '';
                    }
                  }}
                  sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {prerequisites.map((prerequisite, index) => (
                    <Chip
                        key={index}
                        label={prerequisite}
                        onDelete={() => handleDeleteChip(prerequisite, setPrerequisites)}
                    />
                ))}
              </Box>
            </>
        );
      case 4:
        return (
            <>
              <TextField
                  fullWidth
                  label="Pasos de la preparación"
                  placeholder="Añade un paso de la preparación y presiona Enter"
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChip(e.target.value, setPreparationSteps);
                      e.target.value = '';
                    }
                  }}
                  sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {preparationSteps.map((step, index) => (
                    <Chip
                        key={index}
                        label={step}
                        onDelete={() => handleDeleteChip(step, setPreparationSteps)}
                    />
                ))}
              </Box>
            </>
        );
      case 5:
        return (
            <>
              <TextField
                  fullWidth
                  label="Etiquetas"
                  placeholder="Añade una etiqueta y presiona Enter"
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChip(e.target.value, setTags);
                      e.target.value = '';
                    }
                  }}
                  sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {tags.map((tag, index) => (
                    <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleDeleteChip(tag, setTags)}
                    />
                ))}
              </Box>
            </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
      <Box sx={{ width: '100%', p: 3 }}>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                minWidth: 'max-content', // Asegura que el Stepper no se ajuste automáticamente al ancho del contenedor
              }}
          >
            {steps.map((label, index) => (
                <Step
                    key={label}
                    onClick={() => handleStep(index)}
                    sx={{
                      cursor: index <= activeStep ? 'pointer' : 'default',
                      '& .MuiStepLabel-root': {
                        color:
                            completedSteps[index]
                                ? 'rgba(255, 165, 0, 0.5)' // Color principal más claro
                                : undefined,
                      },
                    }}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
            ))}
          </Stepper>
        </Box>
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          {activeStep === steps.length ? (
              <Typography variant="h6" align="center">
                ¡Receta creada exitosamente!
                <Button onClick={handleReset} sx={{ mt: 2 }}>
                  Crear otra receta
                </Button>
              </Typography>
          ) : (
              <>
                {renderStepContent(activeStep)}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button
                      disabled={activeStep === 0 || isSubmitting}
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                  >
                    Atrás
                  </Button>
                  <Button
                      variant="contained"
                      onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                      endIcon={<ArrowForwardIcon />}
                      disabled={!canProceed || isSubmitting} // Validación para deshabilitar el botón
                  >
                    {activeStep === steps.length - 1 ? 'Finalizar' : 'Siguiente'}
                  </Button>
                </Box>
              </>
          )}
        </Paper>

        {/* Popup de confirmación */}
        <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Receta Creada Exitosamente"}</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              ¡Tu receta ha sido guardada con éxito! ¿Qué te gustaría hacer ahora?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              Crear otra receta
            </Button>
            <Button onClick={() => window.location.href = '/me/recipes'} color="primary" autoFocus>
              Ver mis recetas
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default NewRecipePage;